/**
 * @file: chartOption.js
 * @author: liuye11(liuye11@baidu.com)
 */

import format from 'date-fns/format';
import {parseDate} from '@baidu/ve-ria/utils/date';
import {sortBy, flatten, map, min, max, omit} from 'lodash';

function formatDate(date) {
    return format(parseDate(date), 'YYYY-MM-DD');
}

function createYAxisOption(axisInfo, needFormatLabel) {
    let option = {
        type: 'value',
        name: axisInfo.axisName,
        min: axisInfo.axisMin,
        max: axisInfo.axisMax,
        interval: axisInfo.axisInterval,
        axisLabel: {
            formatter(value, index) {
                return index === 0 ? '' : value.toFixed(2);
            },
            textStyle: {
                color: '#999'
            }
        },
        axisTick: {
            show: false
        },
        axisLine: {
            show: false
        },
        splitLine: {
            lineStyle: {
                color: ['#dbdbdb']
            }
        },
        nameTextStyle: {
            color: '#999'
        }
    };
    return needFormatLabel ? option : omit(option, 'axisLabel');
}

/**
 * 计算图表中Y坐标轴相关的一些参数
 *
 * @protected
 * @param {Array.<number>} series Y坐标轴显示的数据
 * @param {string} axisName 坐标轴名称
 * @param {boolean} isIntervalInteger interval是否取整
 * @return {Object}
 */
function computeChartYAxisInfo(series, axisName, isIntervalInteger = false) {
    let axisMin = min(series);
    let axisMax = max(series);
    let axisInterval = (axisMax - axisMin) / 5; // 计算平均值
    axisInterval = isIntervalInteger ? Math.ceil(axisInterval) : axisInterval;
    axisMax = axisInterval * 6 + axisMin; // 6 根横轴，最大上线
    return {axisName, axisMin, axisMax, axisInterval};
}

function getBasicOption(dateList) {
    return {
        grid: {
            left: 80,
            right: 80,
            top: 80
        },
        tooltip: {
            trigger: 'axis'
        },
        xAxis: {
            type: 'category',
            name: '日期',
            boundaryGap: false,
            data: dateList,
            axisTick: {
                show: false
            },
            axisLine: {
                lineStyle: {
                    color: '#dbdbdb',
                    width: 2
                }
            },
            axisLabel: {
                textStyle: {
                    color: '#999'
                }
            },
            nameTextStyle: {
                color: '#999'
            }
        },
        dataZoom: [
            {
                type: 'slider',
                show: true,
                startValue: dateList[0].date,
                endValue: dateList[dateList.length - 1].date
            }
        ]
    };
}

function getSeriesOption(name, data, yAxisIndex) {
    return {
        name,
        data,
        yAxisIndex,
        type: 'line',
        showSymbol: false,
        hoverAnimation: false,
        smooth: true
    };
}
export function getBaseChartOption(seriesData, chartName = '') {
    let data = sortBy(seriesData, item => parseInt(item.date, 10));

    let dateList = [];
    let pvList = [];
    let clickList = [];
    let clickRatioList = [];
    data.forEach(item => {
        dateList.push(formatDate(item.date));
        pvList.push(item.pv);
        clickList.push(item.click);
        clickRatioList.push(item.clickRatio && item.clickRatio.toFixed(2));
    });

    return {
        ...getBasicOption(dateList),
        legend: {
            top: 10,
            right: 80,
            data: [`${chartName}展现`, `${chartName}点击`, `${chartName}点击率`]
        },
        yAxis: [
            createYAxisOption(computeChartYAxisInfo(
                flatten(map(data, item => [item.pv, item.click])),
                '次数',
                true
            )),
            createYAxisOption(computeChartYAxisInfo(
                flatten(map(data, item => [item.clickRatio])),
                '比率'
            ), true)
        ],
        color: ['#758aff', '#3faca3', '#fda550'],
        series: [
            getSeriesOption(`${chartName}展现`, pvList, 0),
            getSeriesOption(`${chartName}点击`, clickList, 0),
            getSeriesOption(`${chartName}点击率`, clickRatioList, 1)
        ]

    };
}

export function getInteractChartOption(seriesData) {
    let data = sortBy(seriesData, item => parseInt(item.date, 10));
    let dateList = [];
    let clickThroughList = [];
    let clickThroughRatioList = [];
    let interactClickList = [];
    let interactClickRatioList = [];

    data.forEach(item => {
        dateList.push(formatDate(item.date));
        clickThroughList.push(item.clickThrough);
        clickThroughRatioList.push(item.clickThroughRatio && item.clickThroughRatio.toFixed(2));
        interactClickList.push(item.interactClick);
        interactClickRatioList.push(item.interactClickRatio && item.interactClickRatio.toFixed(2));
    });

    return {
        ...getBasicOption(dateList),
        legend: {
            top: 10,
            right: 80,
            data: ['跳转点击量', '跳转点击率', '交互点击量', '交互点击率']
        },
        yAxis: [
            createYAxisOption(computeChartYAxisInfo(
                flatten(map(data, item => [item.clickThrough, item.interactClick])),
                '次数',
                true
            )),
            createYAxisOption(computeChartYAxisInfo(
                flatten(map(data, item => [item.clickThroughRatio, item.interactClickRatio])),
                '比率'
            ), true)
        ],
        color: ['#758aff', '#3faca3', '#fda550', '#304ffe'],
        series: [
            getSeriesOption('跳转点击量', clickThroughList, 0),
            getSeriesOption('跳转点击率', clickThroughRatioList, 1),
            getSeriesOption('交互点击量', interactClickList, 0),
            getSeriesOption('交互点击率', interactClickRatioList, 1)
        ]
    };
}
